"use client";

import { useMemo, useState } from "react";

import { useGetBotbPassCompetitionsQuery } from "@/app/redux/features/api/membersHome";
import { useCompetition } from "@/app/redux/features/categories/selectors";
import CompetitionListItem from "./CompetitionListItem";
import CompetitionTabs from "./CompetitionTabs";
import CountdownBar from "./CountdownBar";
import ExclusiveBanner from "./ExclusiveBanner";

export default function CompetitionsSection({ bordered = false }: { bordered?: boolean }) {
  const { data } = useGetBotbPassCompetitionsQuery();
  const [activeTab, setActiveTab] = useState(0);

  const { featured, exclusive } = useMemo(() => {
    if (!data) return { featured: [], exclusive: [] };
    const all = [...(data.played ?? []), ...(data.lost ?? [])];
    return {
      featured: all.filter(c => c.CompetitionTypePrefix !== "SC"),
      exclusive: all.filter(c => c.CompetitionTypePrefix === "SC"),
    };
  }, [data]);

  const nearestCompetitionId = useMemo(() => {
    const visibleItems = activeTab === 0 ? featured : exclusive;
    if (visibleItems.length === 0) return 0;
    const now = Date.now();
    let nearestId = 0;
    let nearestDiff = Infinity;

    for (const comp of visibleItems) {
      const diff = new Date(comp.EndDateTime).getTime() - now;
      if (diff > 0 && diff < nearestDiff) {
        nearestDiff = diff;
        nearestId = comp.CompetitionId;
      }
    }
    return nearestId;
  }, [activeTab, featured, exclusive]);

  const nearestCompetition = useCompetition(nearestCompetitionId ? { CompetitionId: nearestCompetitionId } : undefined);
  const nearestEndDate = nearestCompetition?.EndDate || "";
  const tabs = useMemo(
    () => [
      {
        label: (
          <>
            Featured <br className="block lg:hidden" />
            Competitions
          </>
        ),
        count: featured.length,
        iconSrc: "/assets/images/botb-pass-members/featured-tab-icon.webp",
      },
      {
        label: "Pass Exclusive",
        count: exclusive.length,
        iconSrc: "/assets/images/botb-pass-members/exclusive-tab-icon.webp",
      },
    ],
    [featured.length, exclusive.length]
  );

  const activeCompetitions = activeTab === 0 ? featured : exclusive;
  const totalCount = featured.length + exclusive.length;

  return (
    <section className="flex flex-col gap-6 lg:gap-10 items-center w-full pt-1 lg:pt-1.5">
      <div className="flex flex-col items-center text-center px-4 gap-2 lg:gap-3">
        <div className="flex flex-col items-center gap-1.5 lg:gap-3">
          <div className="inline-flex items-center justify-center px-2 lg:px-3 py-1 lg:py-1.5 border border-[rgba(250,81,19,0.3)] rounded-full">
            <span className="text-[11px] lg:text-base font-medium font-jost leading-3.5 lg:leading-5.5 text-dark-1 whitespace-nowrap">
              🎉 Your name could be next!
            </span>
          </div>
          <h2 className="!m-0 p-0 normal-case text-[24px] lg:text-[32px] font-bold font-jost leading-7.5 lg:leading-10 text-dark-1">
            {`You're currently playing for `}
            <span className="text-primary">
              <br className="block lg:hidden" />
              {totalCount} competitions
            </span>
          </h2>
        </div>
        <p className="m-0 p-0 text-base lg:text-xl font-normal font-jost leading-5.5 lg:leading-6.5 text-secondary">
          Check below the active entries included in your BOTB Pass:
        </p>
      </div>

      <div
        className={`bg-white flex flex-col lg:gap-6 items-center pb-4 lg:pb-6 ${
          bordered
            ? "w-full lg:w-[calc(100%-4rem)] lg:border lg:border-black/8 lg:rounded-2xl lg:overflow-hidden"
            : "w-full"
        }`}>
        <div className="flex flex-col gap-3 lg:gap-4 items-center w-full">
          <CompetitionTabs tabs={tabs} onTabChange={setActiveTab} />

          {nearestEndDate && (
            <CountdownBar
              targetDate={nearestEndDate}
              label={activeTab === 1 ? "Your next BOTB Pass comp ends in:" : "Your next competition ends in:"}
              fullWidth={bordered}
            />
          )}

          {activeTab === 1 && <ExclusiveBanner fullWidth={bordered} />}
        </div>

        <div className={`w-full flex justify-center ${bordered ? "px-4 lg:px-12" : "px-[18px]"}`}>
          <div
            className={
              bordered ? "w-full grid grid-cols-1 lg:grid-cols-2 lg:gap-x-10" : "w-full max-w-[518px] flex flex-col"
            }>
            {activeCompetitions.map((competition, index) => {
              const len = activeCompetitions.length;
              const isLast = index === len - 1;
              // In the 2-column grid the last two items are the bottoms of their
              // respective columns, so the second-to-last must drop its divider on
              // desktop while keeping it on mobile (where it stacks single-column).
              const dividerClassName = bordered && index === len - 2 ? "lg:hidden" : "";
              return (
                <CompetitionListItem
                  key={competition.CompetitionId}
                  competition={competition}
                  isLast={isLast}
                  dividerClassName={dividerClassName}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
